import React from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { HelpTooltip } from './HelpTooltip';

interface RealizationFactorsProps {
  operationalRealizationFactor: number;
  ttfAttributionFactor: number;
  roleVariabilityDiscount: number;
  onOperationalRealizationChange: (value: number) => void;
  onTTFAttributionChange: (value: number) => void;
  onRoleVariabilityChange: (value: number) => void;
  disabled?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export const RealizationFactors: React.FC<RealizationFactorsProps> = ({
  operationalRealizationFactor,
  ttfAttributionFactor,
  roleVariabilityDiscount,
  onOperationalRealizationChange,
  onTTFAttributionChange,
  onRoleVariabilityChange,
  disabled = false,
  isExpanded = false,
  onToggleExpand,
}) => {
  // Calculate effective multipliers
  const ttfMultiplier = ttfAttributionFactor * (1 - roleVariabilityDiscount);
  const averageRealization = (operationalRealizationFactor + ttfMultiplier) / 2;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header - Clickable */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200"
        aria-expanded={isExpanded}
        aria-controls="realization-factors-content"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
                />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-800">Value Realization</h3>
              <p className="text-xs text-gray-600">
                {isExpanded ? 'Fine-tune how benefits are realized in your organization' : `${Math.round(operationalRealizationFactor * 100)}% ops, ${Math.round(ttfAttributionFactor * 100)}% TTF, -${Math.round(roleVariabilityDiscount * 100)}% variance`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(averageRealization * 100)}%
              </div>
              <div className="text-xs text-gray-500">Avg. Realization</div>
            </div>
            {/* Expand/Collapse Icon */}
            <svg 
              className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Collapsible Content */}
      <div
        id="realization-factors-content"
        className={`transition-all duration-500 ease-out ${isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
      >
        <div className="p-6 space-y-8">
      
        {/* Operational Realization Factor */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <label className="text-sm font-semibold text-gray-900">
                Operational Efficiency Capture
              </label>
              <HelpTooltip
                title="Year 1 Realization Rate"
                content="The percentage of calculated operational savings (scheduling time, productivity, cost-per-hire) that you'll actually capture in Year 1."
                details="Conservative: 70% (accounts for adoption friction), Moderate: 80% (typical), Optimistic: 90% (strong change management)"
                position="bottom"
              />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-indigo-600">
                {Math.round(operationalRealizationFactor * 100)}%
              </div>
              <div className="text-xs text-gray-500">of potential</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="pb-5">
              <Slider
                min={60}
                max={100}
                step={5}
                value={operationalRealizationFactor * 100}
                onChange={(value) => onOperationalRealizationChange((value as number) / 100)}
                disabled={disabled}
                marks={{
                  60: '60%',
                  80: '80%',
                  100: '100%',
                }}
                trackStyle={{ backgroundColor: '#0284c7', height: 6 }}
                handleStyle={{
                  borderColor: '#0284c7',
                  backgroundColor: '#0284c7',
                  width: 18,
                  height: 18,
                  marginTop: -6,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                railStyle={{ backgroundColor: '#E5E7EB', height: 6 }}
              />
            </div>
            
            {/* Visual indicator */}
            <div className="flex justify-between items-center px-2">
              <span className="text-xs text-gray-500">Conservative</span>
              <span className="text-xs text-gray-500">Moderate</span>
              <span className="text-xs text-gray-500">Optimistic</span>
            </div>
          </div>
        </div>

        {/* TTF Attribution Factor */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <label className="text-sm font-semibold text-gray-900">
                Scheduling Impact on Time-to-Fill
              </label>
              <HelpTooltip
                title="Direct Attribution"
                content="The portion of time-to-fill improvement directly caused by better scheduling. Other factors like sourcing quality, market conditions, and compensation also affect hiring speed."
                details="Industry research shows scheduling typically accounts for 15-30% of TTF improvements."
              />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-indigo-600">
                {Math.round(ttfAttributionFactor * 100)}%
              </div>
              <div className="text-xs text-gray-500">attribution</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="pb-5">
              <Slider
                min={10}
                max={40}
                step={5}
                value={ttfAttributionFactor * 100}
                onChange={(value) => onTTFAttributionChange((value as number) / 100)}
                disabled={disabled}
                marks={{
                  10: '10%',
                  20: '20%',
                  30: '30%',
                  40: '40%',
                }}
                trackStyle={{ backgroundColor: '#0284c7', height: 6 }}
                handleStyle={{
                  borderColor: '#0284c7',
                  backgroundColor: '#0284c7',
                  width: 18,
                  height: 18,
                  marginTop: -6,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                railStyle={{ backgroundColor: '#E5E7EB', height: 6 }}
              />
            </div>
            
            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                💡 {ttfAttributionFactor >= 0.3 ? 'Aggressive' : ttfAttributionFactor >= 0.2 ? 'Moderate' : 'Conservative'} assumption - scheduling {ttfAttributionFactor >= 0.3 ? 'heavily impacts' : ttfAttributionFactor >= 0.2 ? 'moderately impacts' : 'partially impacts'} your hiring speed
              </p>
            </div>
          </div>
        </div>

        {/* Role Variability Discount */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <label className="text-sm font-semibold text-gray-900">
                Hiring Variance Discount
              </label>
              <HelpTooltip
                title="Portfolio Uncertainty Factor"
                content="Accounts for unpredictability between your planned hiring mix (set in Calculate Values) and actual hiring patterns. Real-world hiring rarely matches plans exactly."
                details="Even with accurate planning, market conditions and business needs cause variance. Conservative: 25-30%, Moderate: 15-20%, Optimistic: 5-10%."
              />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-orange-600">
                -{Math.round(roleVariabilityDiscount * 100)}%
              </div>
              <div className="text-xs text-gray-500">discount</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="pb-5">
              <Slider
                min={0}
                max={50}
                step={5}
                value={roleVariabilityDiscount * 100}
                onChange={(value) => onRoleVariabilityChange((value as number) / 100)}
                disabled={disabled}
                marks={{
                  0: '0%',
                  25: '25%',
                  50: '50%',
                }}
                trackStyle={{ backgroundColor: '#FB923C', height: 6 }}
                handleStyle={{
                  borderColor: '#FB923C',
                  backgroundColor: '#FB923C',
                  width: 18,
                  height: 18,
                  marginTop: -6,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                railStyle={{ backgroundColor: '#E5E7EB', height: 6 }}
              />
            </div>
            
            <div className="flex justify-between items-center px-2">
              <span className="text-xs text-gray-500">Low uncertainty</span>
              <span className="text-xs text-gray-500">Moderate variance</span>
              <span className="text-xs text-gray-500">High unpredictability</span>
            </div>
          </div>
        </div>

        {/* Impact Summary */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Net Impact on ROI Calculations</h4>
          
          <div className="space-y-3">
            {/* Visual bars showing impact */}
            <div className="space-y-2">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">Operational Savings</span>
                  <span className="font-semibold text-indigo-600">{Math.round(operationalRealizationFactor * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full transition-all duration-500"
                    style={{ width: `${operationalRealizationFactor * 100}%` }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">Time-to-Fill Savings</span>
                  <span className="font-semibold text-blue-600">{Math.round(ttfMultiplier * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-500"
                    style={{ width: `${ttfMultiplier * 100}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Summary card */}
            <div className="bg-white/70 rounded-lg p-3 border border-indigo-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Weighted Average Realization</p>
                  <p className="text-xs text-gray-500 mt-0.5">Applied to Year 1 calculations</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                    {Math.round(averageRealization * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};